import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Match } from '../../types/api';
import { isLiveWindowOpen } from '../../utils/amsterdamTime';

interface LiveBadgeProps {
  match: Pick<Match, 'liveStatus' | 'startTime'>;
  /**
   * Mag deze gebruiker een live verslag starten? Dan krijgt ook een wedstrijd zonder verslag
   * een grijze badge, vanaf 36 uur voor de aftrap tot het eind van de wedstrijddag - net als
   * de knop op de detailpagina.
   */
  canStart?: boolean;
}

/**
 * In de wedstrijdenlijst: deze wedstrijd heeft of krijgt een live verslag. Rood met een
 * kloppende stip zolang het loopt; grijs als het afgelopen en terug te kijken is, of als het
 * binnenkort gestart kan worden - zo valt de functie op voordat de wedstrijd begint.
 */
export const LiveBadge: React.FC<LiveBadgeProps> = ({ match, canStart = false }) => {
  const { t } = useTranslation('live');

  const status =
    match.liveStatus ?? (canStart && isLiveWindowOpen(match.startTime, Date.now()) ? 'possible' : null);
  if (!status) return null;

  const loopt = status === 'running';
  const uitleg = t(`badge.${status}`);

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 px-2 py-1 text-xs font-semibold rounded-full',
        loopt ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700',
      )}
      title={uitleg}
    >
      <span className="relative flex h-2 w-2" aria-hidden="true">
        {loopt && (
          <span className="absolute inline-flex h-full w-full rounded-full bg-[#EF4444] opacity-75 motion-safe:animate-ping" />
        )}
        <span
          className={clsx(
            'relative inline-flex h-2 w-2 rounded-full',
            loopt ? 'bg-[#EF4444] motion-safe:animate-live-stip' : 'bg-[#9CA3AF]',
          )}
        />
      </span>
      <span aria-hidden="true">{t('badge.label')}</span>
      <span className="sr-only">{uitleg}</span>
    </span>
  );
};
